import { createSettleGate, SPLASH_GLIDE_SETTLE_MAX_MS } from '../../../ui/splash/splashGlideSettle';
import { useFirstSplashCover } from '../../../ui/splash/useFirstSplashCover';
import { useSplashHold } from '../../../ui/splash/useSplashHold';
import { SPLASH_PHASE, getSplashPhase } from '../../../ui/splash/splashHandoff';
import { useLayoutEffect, useRef, useState } from 'react';

/** Keep in lockstep with `MFA_RESET_NOTE_PREFETCH_CAP_MS` (literal here so
 *  this hook never pulls the note-cache → supabase graph into splash tests). */
const INTRO_CONTENT_CAP_MS = 800;

/**
 * Splash → forced MFA enrollment handoff — gate-side half of the same
 * logoJourney pattern as role welcome (KAN-562 / RD-SEC-021).
 *
 * Why the first cut felt like a "pause going up to the modal":
 *  - we released the splash after ONE layout frame;
 *  - the gate then grew (reset-note intro) while the splash was already
 *    leaving, so the settle gate froze the logo at centre, then re-aimed.
 *
 * Welcome never does that: it mounts under the opaque splash, waits until
 * the wordmark's FINAL rect is still, THEN lets the splash leave so the
 * glide starts in the same breath as the fade — one continuous wordmark.
 *
 * This hook:
 *  - holds the first splash cover until the gate wordmark has settled AND
 *    the intro body is painted (or the note-prefetch cap elapses);
 *  - reports `handoffEntrance` so the card opens with `skipEnter` + fade.
 *
 * Mid-session opens (no splash): hold is a no-op, `handoffEntrance` is false.
 */
export function useMfaEnrollmentHandoff() {
  const covered = useFirstSplashCover();
  const handoffRef = useRef(
    typeof document !== 'undefined' && getSplashPhase() === SPLASH_PHASE.COVERED,
  );
  if (covered) handoffRef.current = true;

  const [readyToLeave, setReadyToLeave] = useState(() => !handoffRef.current);

  useLayoutEffect(() => {
    if (!handoffRef.current) {
      setReadyToLeave(true);
      return undefined;
    }
    let cancelled = false;
    const settled = createSettleGate();
    const started = Date.now();
    // Cap matches the reset-note prefetch bound — never park the splash longer.
    const contentCapMs = INTRO_CONTENT_CAP_MS;
    const hardCapMs = contentCapMs + SPLASH_GLIDE_SETTLE_MAX_MS + 50;

    const tick = () => {
      if (cancelled) return;
      const now = Date.now();
      const wm = typeof document !== 'undefined'
        ? document.querySelector('[data-testid="mfa-gate-wordmark"]')
        : null;
      const intro = typeof document !== 'undefined'
        ? document.querySelector('[data-testid="mfa-gate-intro"]')
        : null;
      // Intro present ⇒ final card height (not the checking placeholder).
      const contentReady = Boolean(intro) || now - started >= contentCapMs;
      if (wm && contentReady) {
        const r = wm.getBoundingClientRect();
        if (r.width > 0 && settled(
          { left: r.left, top: r.top, width: r.width, height: r.height },
          now,
        )) {
          setReadyToLeave(true);
          return;
        }
      }
      if (now - started >= hardCapMs) {
        setReadyToLeave(true);
        return;
      }
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(tick);
      else setReadyToLeave(true);
    };

    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(tick);
    else setReadyToLeave(true);
    return () => { cancelled = true; };
  }, []);

  const holdLive = useSplashHold(Boolean(handoffRef.current) && !readyToLeave);

  return {
    handoffEntrance: Boolean(handoffRef.current),
    holdLive,
    covered,
  };
}

export default useMfaEnrollmentHandoff;
